import { Box, Button as MuiButton, Typography, typographyClasses } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Auth } from 'aws-amplify';
import axios from 'axios';
import { useState } from 'react';
import { configureAmplify } from 'shared/components/auth/utils';
import { pools } from 'shared/components/auth/config';
import { getSignInEmailFromDom } from 'shared/components/auth/my-authenticator/get-sign-in-email-from-dom';
import {
  applySignInEmailFieldError,
  getSignInUsernameInputElement,
  isValidSignInEmail,
} from 'shared/components/auth/my-authenticator/sign-in-email-validation-helpers';
import { getOgpSsoIdentityProvider } from 'shared/services';

type OgpSsoSectionProps = {
  visible: boolean;
};

export const OgpSsoSection = ({ visible }: OgpSsoSectionProps) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(checkEmailMismatch);

  if (!visible) {
    return null;
  }

  const handleSsoClick = async () => {
    sessionStorage.removeItem('is-email-mismatch');
    setErrorMessage(null);
    try {
      const email = getSignInEmailFromDom();
      if (!isValidSignInEmail(email)) {
        const usernameInput = getSignInUsernameInputElement();
        if (usernameInput) {
          applySignInEmailFieldError(usernameInput);
        }
        return;
      }
      const identityProvider = await getOgpSsoIdentityProvider(email);
      if (identityProvider === null) {
        return;
      }
      sessionStorage.setItem('sso-sign-in-email', email.trim());
      configureAmplify(pools.ogp);
      await Auth.federatedSignIn({
        customProvider: identityProvider,
        customState: 'signed-in-via-sso',
      });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else {
        setErrorMessage('Er is een fout opgetreden. Probeer het opnieuw.');
      }
    }
  };

  return (
    <Box mt={2} mb={2} width={'100%'}>
      <Box display={'flex'} alignItems={'center'} width={'100%'} mb={2} gap={1.5}>
        <Box flex={1} borderBottom={'1px solid'} borderColor={'grey.300'} />
        <Typography
          component="span"
          variant="body2"
          color={'grey.600'}
          px={1.5}
          bgcolor={'background.paper'}
          whiteSpace={'nowrap'}
        >
          Of
        </Typography>
        <Box flex={1} borderBottom={'1px solid'} borderColor={'grey.300'} />
      </Box>
      <StyledSsoButton
        fullWidth
        variant="outlined"
        onClick={() => {
          handleSsoClick();
        }}
      >
        Inloggen met SSO
      </StyledSsoButton>
      {errorMessage && <StyledErrorMessage variant="body2">{errorMessage}</StyledErrorMessage>}
    </Box>
  );
};

const checkEmailMismatch = () => {
  if (sessionStorage.getItem('is-email-mismatch')) {
    return 'Het e-mailadres komt niet overeen met het vorige e-mailadres.';
  }
  return null;
};
const StyledSsoButton = styled(MuiButton)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  color: theme.palette.common.black,
  border: `1px solid ${theme.palette.common.black}`,
  borderRadius: 9999,
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  fontWeight: theme.typography.fontWeightMedium,
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.common.black,
    border: `1px solid ${theme.palette.common.black}`,
    boxShadow: 'none',
  },
}));

const StyledErrorMessage = styled(Typography)(({ theme }) => ({
  [`&.${typographyClasses.root}`]: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
}));
